import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  ActionIcon,
  Button,
  Card,
  Flex,
  Group,
  Loader,
  Select,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";

import { Can, EmptyMessage } from "@components/shared";
import { isOrgStaffCatalogRole, PERMISSIONS } from "@util/permissions";

export default function OrganizationUserRolesPermissions({
  organizationId,
  userId,
}) {
  const { t } = useTranslation();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [selectedPermissionId, setSelectedPermissionId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);

  useEffect(() => {
    fetchData();
  }, [organizationId, userId]);

  function fetchData() {
    setLoading(true);
    Promise.all([
      axios.post(
        `/organizations/${organizationId}/users/${userId}/retrieve-roles/`,
        { page: 0, page_size: 100 }
      ),
      axios.post(
        `/organizations/${organizationId}/users/${userId}/retrieve-permissions/`,
        { page: 0, page_size: 200 }
      ),
      axios.get(`/roles/`),
      axios.post(`/retrieve-permissions/`, {
        scope: "all",
        page: 0,
        page_size: 500,
      }),
    ])
      .then(([rolesRes, permsRes, allRolesRes, catalogRes]) => {
        const assignedRoles = rolesRes.data.response[0]?.data || [];
        const assignedPerms = permsRes.data.response[0]?.data || [];
        setRoles(assignedRoles);
        setPermissions(assignedPerms);

        const assignedRoleIds = new Set(
          assignedRoles.map((row) => String(row.role_id || row.role?.id))
        );
        setAvailableRoles(
          (allRolesRes.data.response || [])
            .filter((role) => !isOrgStaffCatalogRole(role))
            .filter((role) => !assignedRoleIds.has(String(role.id)))
            .map((role) => ({ value: String(role.id), label: role.name }))
        );

        const assignedPermissionIds = new Set(
          assignedPerms.map((row) =>
            String(row.permission_id || row.permission?.id)
          )
        );
        setAvailablePermissions(
          (catalogRes.data.response[0]?.data || [])
            .filter((permission) => !assignedPermissionIds.has(String(permission.id)))
            .map((permission) => ({
              value: String(permission.id),
              label: permission.key
                ? `${permission.name} (${permission.key})`
                : permission.name,
            }))
        );
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  function addRole() {
    if (!selectedRoleId) return;
    setBusyKey(`role-add-${selectedRoleId}`);
    axios
      .post(`/organizations/${organizationId}/users/${userId}/roles/`, {
        role_id: selectedRoleId,
      })
      .then(() => {
        setSelectedRoleId(null);
        setBusyKey(null);
        toast.success(t("updated"));
        fetchData();
      })
      .catch((err) => {
        setBusyKey(null);
        toast.error(err);
      });
  }

  function removeRole(roleId) {
    setBusyKey(`role-remove-${roleId}`);
    axios
      .post(`/organizations/${organizationId}/users/${userId}/remove-role/`, {
        role_id: roleId,
      })
      .then(() => {
        setBusyKey(null);
        toast.success(t("updated"));
        fetchData();
      })
      .catch((err) => {
        setBusyKey(null);
        toast.error(err);
      });
  }

  function addPermission() {
    if (!selectedPermissionId) return;
    setBusyKey(`perm-add-${selectedPermissionId}`);
    axios
      .post(`/organizations/${organizationId}/users/${userId}/permissions/`, {
        permission_id: selectedPermissionId,
      })
      .then(() => {
        setSelectedPermissionId(null);
        setBusyKey(null);
        toast.success(t("updated"));
        fetchData();
      })
      .catch((err) => {
        setBusyKey(null);
        toast.error(err);
      });
  }

  function removePermission(permissionId) {
    setBusyKey(`perm-remove-${permissionId}`);
    axios
      .post(
        `/organizations/${organizationId}/users/${userId}/remove-permission/`,
        { permission_id: permissionId }
      )
      .then(() => {
        setBusyKey(null);
        toast.success(t("updated"));
        fetchData();
      })
      .catch((err) => {
        setBusyKey(null);
        toast.error(err);
      });
  }

  if (loading) {
    return (
      <Group justify="center" py="xl">
        <Loader size="sm" />
      </Group>
    );
  }

  return (
    <Can permission={PERMISSIONS.ORGANIZATION_ADMINS_MANAGE}>
      <Stack gap="lg">
        <Stack gap="md">
          <Title order={5}>{t("role_plural")}</Title>
          <Card>
            <Flex gap="sm" align="flex-end">
              <Select
                style={{ flex: 1 }}
                searchable
                clearable
                placeholder={t("role_singular")}
                data={availableRoles}
                value={selectedRoleId}
                onChange={setSelectedRoleId}
              />
              <Button
                leftSection={<IconPlus size={14} />}
                disabled={!selectedRoleId}
                loading={busyKey === `role-add-${selectedRoleId}`}
                onClick={addRole}
              >
                {t("add")}
              </Button>
            </Flex>
          </Card>
          <Stack gap="xs">
            {!roles.length && <EmptyMessage />}
            {roles.map((row) => {
              const roleId = row.role_id || row.role?.id;
              return (
                <Card key={row.id || roleId}>
                  <Flex gap="sm" align="center">
                    <Text fw={700} style={{ flexGrow: 1 }}>
                      {row.role?.name || roleId}
                    </Text>
                    <ActionIcon
                      size="sm"
                      radius="xl"
                      variant="light"
                      color="red"
                      loading={busyKey === `role-remove-${roleId}`}
                      onClick={() => removeRole(roleId)}
                    >
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Flex>
                </Card>
              );
            })}
          </Stack>
        </Stack>

        <Stack gap="md">
          <Title order={5}>{t("permission_plural")}</Title>
          <Card>
            <Flex gap="sm" align="flex-end">
              <Select
                style={{ flex: 1 }}
                searchable
                clearable
                placeholder={t("permission_singular")}
                data={availablePermissions}
                value={selectedPermissionId}
                onChange={setSelectedPermissionId}
              />
              <Button
                leftSection={<IconPlus size={14} />}
                disabled={!selectedPermissionId}
                loading={busyKey === `perm-add-${selectedPermissionId}`}
                onClick={addPermission}
              >
                {t("add")}
              </Button>
            </Flex>
          </Card>
          <Stack gap="xs">
            {!permissions.length && <EmptyMessage />}
            {permissions.map((row) => {
              const permissionId = row.permission_id || row.permission?.id;
              return (
                <Card key={row.id || permissionId}>
                  <Flex gap="sm" align="center">
                    <div style={{ flexGrow: 1 }}>
                      <Text
                        fw={700}
                        component={Link}
                        to={`/permissions/${permissionId}`}
                        c="dark"
                        style={{ textDecoration: "none" }}
                      >
                        {row.permission?.name || permissionId}
                      </Text>
                      {row.permission?.description && (
                        <Text size="sm" c="dimmed">
                          {row.permission.description}
                        </Text>
                      )}
                    </div>
                    <ActionIcon
                      size="sm"
                      radius="xl"
                      variant="light"
                      color="red"
                      loading={busyKey === `perm-remove-${permissionId}`}
                      onClick={() => removePermission(permissionId)}
                    >
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Flex>
                </Card>
              );
            })}
          </Stack>
        </Stack>
      </Stack>
    </Can>
  );
}
